import { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { api } from "@/lib/api";
import { Card, PageHeader, Pill, num, brl } from "./_shared";
import { ChevronLeft, MapPin, Users, DollarSign } from "lucide-react";

const GEO_URL = "/brazil-states-simplified.json";

const UF_TO_NAME = {
  AC:"Acre", AL:"Alagoas", AP:"Amapá", AM:"Amazonas", BA:"Bahia", CE:"Ceará",
  DF:"Distrito Federal", ES:"Espírito Santo", GO:"Goiás", MA:"Maranhão",
  MT:"Mato Grosso", MS:"Mato Grosso do Sul", MG:"Minas Gerais", PA:"Pará",
  PB:"Paraíba", PR:"Paraná", PE:"Pernambuco", PI:"Piauí", RJ:"Rio de Janeiro",
  RN:"Rio Grande do Norte", RS:"Rio Grande do Sul", RO:"Rondônia", RR:"Roraima",
  SC:"Santa Catarina", SP:"São Paulo", SE:"Sergipe", TO:"Tocantins",
};

const METRICS = [
  { k: "users",   l: "Usuários",   fmt: num,  icon: Users,     accent: "#d4a24c" },
  { k: "revenue", l: "Receita",    fmt: brl,  icon: DollarSign, accent: "#10b981" },
];

export default function AdminAnalytics() {
  const [geo, setGeo] = useState(null);       // /api/admin/geo response
  const [metric, setMetric] = useState("users");
  const [selectedUF, setSelectedUF] = useState(null);
  const [hovered, setHovered] = useState(null); // { uf, name, value }
  const [compareMode, setCompareMode] = useState(false);
  const [compareSet, setCompareSet] = useState([]); // ["MG", "SP", ...]

  useEffect(() => { api.get("/admin/geo").then(r => setGeo(r.data)); }, []);

  // Per-UF value map for coloring
  const values = useMemo(() => {
    const out = {};
    if (!geo) return out;
    if (metric === "users") {
      for (const s of geo.by_state || []) {
        const code = (s.code || "").toUpperCase();
        if (code) out[code] = s.users || 0;
      }
    } else {
      for (const [code, amount] of Object.entries(geo.revenue_by_state_cents || {})) {
        if (code) out[(code || "").toUpperCase()] = amount || 0;
      }
    }
    return out;
  }, [geo, metric]);

  const maxValue = Math.max(1, ...Object.values(values));
  const colorScale = scaleLinear()
    .domain([0, maxValue])
    .range(["#161b26", "#d4a24c"]);

  const currentMetric = METRICS.find(m => m.k === metric);
  const Icon = currentMetric.icon;

  const citiesForSelected = useMemo(() => {
    if (!selectedUF || !geo?.by_city) return [];
    return geo.by_city.filter(c => (c.state || "").toUpperCase() === selectedUF).slice(0, 15);
  }, [selectedUF, geo]);

  return (
    <div className="p-3 sm:p-6 lg:p-8" data-testid="admin-analytics">
      <PageHeader
        title="Hermes Analytics"
        subtitle="Inteligência Administrativa e Financeira — Mapa interativo com drill-down Brasil → Estado → Cidade"
        right={
          <div className="flex bg-[#0f1524] border border-white/5 rounded-xl p-1" data-testid="analytics-metric-tabs">
            {METRICS.map(m => (
              <button key={m.k} onClick={() => setMetric(m.k)}
                className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 ${metric === m.k ? "bg-[#d4a24c] text-black font-semibold" : "text-slate-300 hover:bg-white/5"}`}
                data-testid={`analytics-metric-${m.k}`}>
                <m.icon className="w-3.5 h-3.5"/> {m.l}
              </button>
            ))}
          </div>
        }
      />

      {/* Breadcrumb + filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4 text-xs" data-testid="analytics-breadcrumb">
        <button onClick={() => setSelectedUF(null)}
          className={`px-3 py-1 rounded-full border ${selectedUF ? "border-white/10 text-slate-300 hover:bg-white/5" : "border-[#d4a24c] text-[#d4a24c] bg-[#3a2a15]"}`}
          data-testid="analytics-crumb-brazil">
          BRASIL
        </button>
        {selectedUF && (
          <>
            <span className="text-slate-500">→</span>
            <span className="px-3 py-1 rounded-full border border-[#d4a24c] text-[#d4a24c] bg-[#3a2a15]" data-testid="analytics-crumb-uf">
              {UF_TO_NAME[selectedUF] || selectedUF} · {selectedUF}
            </span>
            <button onClick={() => setSelectedUF(null)} className="ml-1 text-slate-400 hover:text-slate-100 flex items-center gap-1" data-testid="analytics-back">
              <ChevronLeft className="w-3 h-3"/> voltar
            </button>
          </>
        )}
        <div className="flex-1"/>
        <button onClick={()=>{setCompareMode(m => !m); if(compareMode) setCompareSet([]);}}
          className={`px-3 py-1 rounded-full border text-xs ${compareMode ? "bg-emerald-600 text-white border-emerald-500" : "border-white/10 text-slate-300 hover:bg-white/5"}`}
          data-testid="analytics-compare-toggle">
          {compareMode ? `Comparando (${compareSet.length})` : "Comparar estados"}
        </button>
        {compareMode && compareSet.length > 0 && (
          <button onClick={()=>setCompareSet([])} className="text-slate-400 hover:text-slate-100 text-xs" data-testid="analytics-compare-clear">
            limpar
          </button>
        )}
      </div>

      {compareMode && compareSet.length >= 2 && (
        <Card className="mb-4" title="Comparativo entre estados" kicker={`${compareSet.length} selecionado(s)`} testid="analytics-compare-panel">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                  <th className="py-2 pr-4">Estado</th>
                  <th className="py-2 pr-4 text-right">Usuários</th>
                  <th className="py-2 pr-4 text-right">Receita</th>
                  <th className="py-2 pr-4 text-right">Cidades</th>
                  <th className="py-2 pr-4 text-right">Ticket médio</th>
                </tr>
              </thead>
              <tbody>
                {compareSet.map(uf => {
                  const st = (geo?.by_state || []).find(s => (s.code||"").toUpperCase() === uf) || {};
                  const users = st.users || 0;
                  const revenue = geo?.revenue_by_state_cents?.[uf] || 0;
                  const cities = (geo?.by_city || []).filter(c => (c.state||"").toUpperCase() === uf).length;
                  const ticket = users ? Math.round(revenue / users) : 0;
                  return (
                    <tr key={uf} className="border-b border-white/5" data-testid={`compare-row-${uf}`}>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"/>
                          <div>
                            <div className="text-slate-100">{UF_TO_NAME[uf] || uf}</div>
                            <div className="text-[10px] text-slate-500">{uf}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-right text-slate-100">{num(users)}</td>
                      <td className="py-3 pr-4 text-right text-emerald-400 font-semibold">{brl(revenue)}</td>
                      <td className="py-3 pr-4 text-right text-slate-300">{cities}</td>
                      <td className="py-3 pr-4 text-right text-[#d4a24c]">{brl(ticket)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Map */}
        <Card className="lg:col-span-3" title="Mapa do Brasil" kicker="Clique num estado para detalhar" testid="analytics-map">
          <div className="relative bg-[#0a0d17] rounded-xl border border-white/5 overflow-hidden" style={{ height: 500 }}>
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 700, center: [-54, -15] }}
              style={{ width: "100%", height: "100%" }}
            >
              <ZoomableGroup
                center={selectedUF ? computeCenter(selectedUF) : [-54, -15]}
                zoom={selectedUF ? 3 : 1}
                minZoom={1}
                maxZoom={8}
              >
                <Geographies geography={GEO_URL}>
                  {({ geographies }) => geographies.map((g) => {
                    const uf = (g.properties?.UF || "").toUpperCase();
                    const val = values[uf] || 0;
                    const isSelected = selectedUF === uf;
                    const isDimmed = selectedUF && !isSelected;
                    return (
                      <Geography
                        key={g.rsmKey}
                        geography={g}
                        onClick={() => {
                          if (compareMode) {
                            setCompareSet(cs => cs.includes(uf) ? cs.filter(x => x !== uf) : [...cs, uf]);
                          } else {
                            setSelectedUF(uf === selectedUF ? null : uf);
                          }
                        }}
                        onMouseEnter={() => setHovered({ uf, name: g.properties?.ESTADO, value: val })}
                        onMouseLeave={() => setHovered(null)}
                        style={{
                          default: {
                            fill: compareSet.includes(uf) ? "#10b981" : (val > 0 ? colorScale(val) : "#161b26"),
                            stroke: isSelected || compareSet.includes(uf) ? "#d4a24c" : "#0a0d17",
                            strokeWidth: (isSelected || compareSet.includes(uf)) ? 1.4 : 0.4,
                            outline: "none",
                            opacity: (isDimmed && !compareSet.includes(uf)) ? 0.35 : 1,
                            cursor: "pointer",
                          },
                          hover: {
                            fill: "#d4a24c",
                            stroke: "#fff",
                            strokeWidth: 0.6,
                            outline: "none",
                            cursor: "pointer",
                          },
                          pressed: { fill: "#c48a3e", outline: "none" },
                        }}
                        data-testid={`geo-${uf}`}
                      />
                    );
                  })}
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
            {hovered && (
              <div className="absolute top-3 right-3 bg-[#0f1524]/95 backdrop-blur border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 shadow-xl" data-testid="analytics-tooltip">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{hovered.uf}</div>
                <div className="font-semibold">{hovered.name || UF_TO_NAME[hovered.uf]}</div>
                <div className="text-[#d4a24c]">{currentMetric.fmt(hovered.value)}</div>
              </div>
            )}
            {/* Legend */}
            <div className="absolute bottom-3 left-3 bg-[#0f1524]/95 backdrop-blur border border-white/10 rounded-xl px-3 py-2 text-[10px] text-slate-400 flex items-center gap-2" data-testid="analytics-legend">
              <span>menos</span>
              <div className="w-24 h-2 rounded-full bg-gradient-to-r from-[#161b26] to-[#d4a24c]"/>
              <span>mais</span>
            </div>
          </div>
        </Card>

        {/* Detail column */}
        <div className="lg:col-span-2 space-y-4">
          <Card title={selectedUF ? `${UF_TO_NAME[selectedUF]} · ${selectedUF}` : "Ranking de estados"}
                kicker={selectedUF ? "Drill-down por cidade" : "Top 10"} testid="analytics-ranking">
            {!selectedUF ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/5">
                    <th className="py-2 pr-2">#</th>
                    <th className="py-2 pr-2">UF</th>
                    <th className="py-2 pr-2 text-right">{currentMetric.l}</th>
                  </tr>
                </thead>
                <tbody>
                  {topStates(values, 10).map((row, i) => (
                    <tr key={row.uf} className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                      onClick={() => setSelectedUF(row.uf)}
                      data-testid={`analytics-state-row-${row.uf}`}>
                      <td className="py-2 pr-2 text-slate-500">{i+1}</td>
                      <td className="py-2 pr-2">
                        <div className="text-slate-100">{UF_TO_NAME[row.uf] || row.uf}</div>
                        <div className="text-[10px] text-slate-500">{row.uf}</div>
                      </td>
                      <td className="py-2 pr-2 text-right text-[#d4a24c] font-semibold">{currentMetric.fmt(row.value)}</td>
                    </tr>
                  ))}
                  {topStates(values, 10).length === 0 && (
                    <tr><td colSpan="3" className="py-6 text-center text-slate-500">Sem dados</td></tr>
                  )}
                </tbody>
              </table>
            ) : (
              <div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="rounded-lg bg-[#0a0d17] border border-white/5 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Usuários</div>
                    <div className="text-2xl font-serif text-slate-100">{num((geo?.by_state || []).find(s => (s.code||"").toUpperCase() === selectedUF)?.users || 0)}</div>
                  </div>
                  <div className="rounded-lg bg-[#0a0d17] border border-white/5 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Receita</div>
                    <div className="text-2xl font-serif text-[#d4a24c]">{brl(geo?.revenue_by_state_cents?.[selectedUF] || 0)}</div>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Cidades</div>
                <div className="max-h-64 overflow-y-auto">
                  {citiesForSelected.length === 0 && <div className="text-sm text-slate-500 py-3">Nenhuma cidade cadastrada neste estado.</div>}
                  {citiesForSelected.map((c, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5" data-testid={`analytics-city-${i}`}>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-slate-500"/>
                        <span className="text-sm text-slate-100">{c.city || "(sem cidade)"}</span>
                      </div>
                      <span className="text-xs text-[#d4a24c] font-semibold">{num(c.users)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          <Card kicker="Insights rápidos" title="Como está a distribuição" testid="analytics-insights">
            <ul className="text-xs text-slate-400 space-y-2">
              <li>• Estados com dados: <span className="text-slate-100">{Object.values(values).filter(v => v > 0).length}</span> de 27</li>
              <li>• Maior concentração: <span className="text-slate-100">{topStates(values, 1)[0] ? `${UF_TO_NAME[topStates(values,1)[0].uf]} (${currentMetric.fmt(topStates(values,1)[0].value)})` : "—"}</span></li>
              <li>• Total {currentMetric.l.toLowerCase()}: <span className="text-slate-100">{currentMetric.fmt(Object.values(values).reduce((a,b)=>a+b,0))}</span></li>
              <li>• Cidades cadastradas: <span className="text-slate-100">{(geo?.by_city || []).length}</span></li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}

function topStates(values, n) {
  return Object.entries(values)
    .map(([uf, value]) => ({ uf, value }))
    .filter(x => x.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, n);
}

// Rough centroid coords for zoom-in when a UF is selected
const UF_CENTERS = {
  AC:[-70,-9], AL:[-36.5,-9.5], AP:[-52,1.5], AM:[-64,-4], BA:[-42,-12.5],
  CE:[-39.5,-5], DF:[-47.9,-15.8], ES:[-40.3,-20], GO:[-49.5,-15.5], MA:[-45,-5],
  MT:[-55,-13], MS:[-54.5,-20.5], MG:[-44,-18], PA:[-52,-4], PB:[-36.5,-7],
  PR:[-51.5,-24.5], PE:[-37.5,-8.5], PI:[-43,-7], RJ:[-43.2,-22.5], RN:[-36.8,-5.7],
  RS:[-53.5,-30], RO:[-63,-11], RR:[-61,2], SC:[-50.5,-27.5], SP:[-48.5,-22.5],
  SE:[-37.4,-10.5], TO:[-48.5,-10],
};

function computeCenter(uf) {
  return UF_CENTERS[uf] || [-54, -15];
}
